import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Filter, BarChart3, Table, Loader2 } from 'lucide-react';
import {
  WidgetFilterChips,
  WidgetMenu,
  DataGrid,
  DataGridColumnHeader,
  getDetailGridConfig,
  applyDetailGridConfig,
  type DetailGridColumn,
} from '@t-one-internal/design-system';
import type { ColumnDef, PaginationState, SortingState } from '@tanstack/react-table';
import { WidgetFilterSelector } from './WidgetFilterSelector';
import { useDrillToDetail } from '../../hooks/useDrillToDetail';
import type { QueryFilter, WidgetFilterConfig, ChartType, WidgetCapabilities, TimeRange } from '../../types/analytics';
import type { DrillToDetailContext } from '../../types/drillToDetail';

interface WidgetModalProps {
  widgetId: string | number;
  title: string;
  /** The widget content to display in the modal */
  children?: React.ReactNode;
  filters?: QueryFilter[];
  availableFilters?: WidgetFilterConfig[];
  fieldLabels?: Record<string, string>;
  onClose: () => void;
  onFiltersChange?: (filters: QueryFilter[]) => Promise<void>;
  /** Widget capabilities (for chart type options) */
  capabilities?: WidgetCapabilities | null;
  /** Current chart type */
  currentChartType?: ChartType;
  /** Callback when chart type changes */
  onChartTypeChange?: (chartType: ChartType) => void;
  /** Callback when title changes */
  onTitleChange?: (newTitle: string) => Promise<void>;
  /** Whether to open the filter selector immediately */
  initialFilterOpen?: boolean;
  /** Metric key for fetching detailed data (enables grid view) */
  metricKey?: string;
  /** Time range for detailed data query */
  timeRange?: TimeRange;
  /** Whether to show the filter icon (default: true) */
  showFilterIcon?: boolean;
  /** Whether to show the table/grid view toggle (default: true) */
  showTableView?: boolean;
  /** Widget scoping value for data visibility */
  scoping?: string;
  /** Public share context — passed into drill-to-detail so it uses public API */
  publicContext?: { ownerUserId: string; accountId: string; shareToken: string };
}

/**
 * Widget Modal Component
 *
 * Fullscreen modal view of a widget chart.
 */
export const WidgetModal: React.FC<WidgetModalProps> = ({
  widgetId,
  title,
  children,
  filters = [],
  availableFilters = [],
  fieldLabels = {},
  onClose,
  onFiltersChange,
  capabilities,
  currentChartType,
  onChartTypeChange,
  onTitleChange,
  initialFilterOpen = false,
  metricKey,
  timeRange,
  showFilterIcon = true,
  showTableView = true,
  scoping,
  publicContext,
}) => {
  const [filterSelectorOpen, setFilterSelectorOpen] = useState(initialFilterOpen);
  const [showFilters, setShowFilters] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // View mode state for chart/grid toggle
  const [viewMode, setViewMode] = useState<'chart' | 'grid'>('chart');
  const [gridInitialized, setGridInitialized] = useState(false);

  // Drill-to-detail hook for grid data
  const drillToDetail = useDrillToDetail({ pageSize: 100 });

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  // Update editedTitle when title prop changes
  useEffect(() => {
    setEditedTitle(title);
  }, [title]);

  // Handle escape key (don't close if editing title)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isEditingTitle) {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, isEditingTitle]);

  // Lazy load grid data when switching to grid view
  useEffect(() => {
    if (viewMode === 'grid' && !gridInitialized && metricKey) {
      const context: DrillToDetailContext = {
        metricKey,
        widgetTitle: title,
        existingFilters: filters,
        timeRange,
        scoping,
        publicContext,
        // NO clickFilter - grid shows all widget data
      };
      drillToDetail.initialize(context);
      setGridInitialized(true);
    }
  }, [viewMode, gridInitialized, metricKey, title, filters, timeRange]);

  // Re-fetch grid data when filters change while in grid view
  useEffect(() => {
    if (viewMode === 'grid' && gridInitialized && metricKey) {
      const context: DrillToDetailContext = {
        metricKey,
        widgetTitle: title,
        existingFilters: filters,
        timeRange,
        scoping,
        publicContext,
      };
      drillToDetail.initialize(context);
    }
  }, [filters]);

  // Format cell value for display
  const formatCellValue = (value: unknown, column: DetailGridColumn): string => {
    if (value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const formatDate = (d: Date) => {
      const day = String(d.getDate()).padStart(2, '0');
      return `${day}-${months[d.getMonth()]}-${d.getFullYear()}`;
    };
    // Handle datetime/date types (V2 schema uses DATETIME/DATE, legacy uses TIMESTAMP/LONG)
    const dt = column.data_type;
    if (dt === 'DATETIME' || dt === 'DATE' || dt === 'TIMESTAMP' || dt === 'TIMESTAMPTZ' || dt === 'LONG') {
      // ISO string (e.g. "2025-11-19T06:15:01Z")
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        const date = new Date(value);
        if (!isNaN(date.getTime())) return formatDate(date);
      }
      // Numeric timestamp (milliseconds)
      const num = Number(value);
      if (!isNaN(num) && num > 1000000000000) {
        return formatDate(new Date(num));
      }
    }
    return String(value);
  };

  // Get centralized config and apply to columns
  const displayColumns = useMemo(() => {
    if (!metricKey) return drillToDetail.columns as DetailGridColumn[];
    const config = getDetailGridConfig(metricKey);
    return applyDetailGridConfig(drillToDetail.columns as DetailGridColumn[], config);
  }, [drillToDetail.columns, metricKey]);

  // Convert columns to DataGrid ColumnDef format
  const gridColumns = useMemo((): ColumnDef<Record<string, unknown>>[] => {
    return displayColumns.map((col) => ({
      id: col.name,
      accessorKey: col.name,
      header: ({ column }) => (
        <DataGridColumnHeader column={column} title={col.label} />
      ),
      cell: ({ row }) => {
        const value = row.getValue(col.name);
        return <span>{formatCellValue(value, col)}</span>;
      },
      enableSorting: col.sortable,
    }));
  }, [displayColumns]);

  // Pagination state for DataGrid
  const pagination: PaginationState = useMemo(() => ({
    pageIndex: drillToDetail.currentPage - 1, // DataGrid uses 0-indexed pages
    pageSize: 100,
  }), [drillToDetail.currentPage]);

  // Sorting state for DataGrid
  const sorting: SortingState = useMemo(() => {
    if (!drillToDetail.sortState) return [];
    return [{
      id: drillToDetail.sortState.field,
      desc: drillToDetail.sortState.direction === 'desc',
    }];
  }, [drillToDetail.sortState]);

  // Handle pagination change
  const handlePaginationChange = (updater: PaginationState | ((old: PaginationState) => PaginationState)) => {
    const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
    const newPage = newPagination.pageIndex + 1; // Convert back to 1-indexed
    if (newPage !== drillToDetail.currentPage) {
      drillToDetail.fetchPage(newPage);
    }
  };

  // Handle sorting change
  const handleSortingChange = (updater: SortingState | ((old: SortingState) => SortingState)) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      // Only trigger if actually changed
      if (drillToDetail.sortState?.field !== id || (drillToDetail.sortState?.direction === 'desc') !== desc) {
        drillToDetail.sortBy(id);
      }
    }
  };

  // Handle modal close - reset state
  const handleClose = () => {
    setViewMode('chart');
    setGridInitialized(false);
    drillToDetail.reset();
    onClose();
  };

  const handleTitleDoubleClick = () => {
    if (onTitleChange) {
      setIsEditingTitle(true);
    }
  };

  const handleTitleSave = async () => {
    const trimmedTitle = editedTitle.trim();
    if (trimmedTitle && trimmedTitle !== title && onTitleChange) {
      await onTitleChange(trimmedTitle);
    } else {
      setEditedTitle(title);
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTitleSave();
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setEditedTitle(title);
      setIsEditingTitle(false);
    }
  };

  const handleRemoveFilter = async (field: string) => {
    if (onFiltersChange) {
      const updatedFilters = filters.filter((f) => f.field !== field);
      await onFiltersChange(updatedFilters);
    }
  };

  const handleSaveFilters = async (newFilters: QueryFilter[]) => {
    if (onFiltersChange) {
      await onFiltersChange(newFilters);
    }
    setFilterSelectorOpen(false);
  };

  const filterCount = filters.length;
  // Show filter icon based on prop - some pages (like My Portal) may not need filters
  const hasFiltersAvailable = showFilterIcon;

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '40px',
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          width: viewMode === 'grid' ? '1000px' : '700px',
          height: viewMode === 'grid' ? '600px' : '500px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          transition: 'width 0.2s ease, height 0.2s ease',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#f9fafb',
            flexShrink: 0,
          }}
        >
          {isEditingTitle ? (
            <input
              ref={titleInputRef}
              type="text"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              onBlur={handleTitleSave}
              onKeyDown={handleTitleKeyDown}
              style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#111827',
                margin: 0,
                padding: '4px 8px',
                border: '1px solid #3b82f6',
                borderRadius: '6px',
                outline: 'none',
                backgroundColor: 'white',
                minWidth: '150px',
                maxWidth: '400px',
              }}
            />
          ) : (
            <h2
              onDoubleClick={handleTitleDoubleClick}
              style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#111827',
                margin: 0,
                cursor: onTitleChange ? 'text' : 'default',
              }}
              title={onTitleChange ? 'Double-click to edit' : undefined}
            >
              {title}
            </h2>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* View Mode Toggle - Only show if metricKey is available AND showTableView is true */}
            {metricKey && showTableView && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '8px',
                  padding: '2px',
                }}
              >
                <button
                  onClick={() => setViewMode('chart')}
                  style={{
                    padding: '6px 10px',
                    backgroundColor: viewMode === 'chart' ? 'white' : 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    boxShadow: viewMode === 'chart' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                  }}
                  title="Chart View"
                >
                  <BarChart3 size={16} style={{ color: viewMode === 'chart' ? '#1e40af' : '#6b7280' }} />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  style={{
                    padding: '6px 10px',
                    backgroundColor: viewMode === 'grid' ? 'white' : 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    boxShadow: viewMode === 'grid' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                  }}
                  title="Table View"
                >
                  <Table size={16} style={{ color: viewMode === 'grid' ? '#1e40af' : '#6b7280' }} />
                </button>
              </div>
            )}
            {/* Filter Toggle Button */}
            {hasFiltersAvailable && (
              <button
                onClick={() => setShowFilters(!showFilters)}
                style={{
                  position: 'relative',
                  padding: '8px',
                  backgroundColor: showFilters ? '#dbeafe' : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!showFilters) {
                    e.currentTarget.style.backgroundColor = '#e5e7eb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!showFilters) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
                title={showFilters ? 'Hide Filters' : 'Show Filters'}
              >
                <Filter size={18} style={{ color: showFilters ? '#1e40af' : '#6b7280' }} />
                {!showFilters && filterCount > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      minWidth: '16px',
                      height: '16px',
                      padding: '0 4px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                    }}
                  >
                    {filterCount}
                  </span>
                )}
              </button>
            )}
            {/* Widget Menu (Chart Type only) - Only show if multiple chart types available AND in chart view */}
            {viewMode === 'chart' && (() => {
              const visualization = capabilities?.visualization;
              const allowedChartTypes = visualization?.allowedChartTypes || visualization?.allowed_chart_types || [];
              const hasMultipleChartTypes = allowedChartTypes.length > 1 && onChartTypeChange;
              return hasMultipleChartTypes ? (
                <WidgetMenu
                  widgetId={String(widgetId)}
                  capabilities={capabilities}
                  currentChartType={currentChartType}
                  onChartTypeChange={onChartTypeChange}
                />
              ) : null;
            })()}
            {/* Close Button */}
            <button
              onClick={handleClose}
              style={{
                padding: '8px',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e5e7eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              title="Close (Esc)"
            >
              <X size={20} style={{ color: '#6b7280' }} />
            </button>
          </div>
        </div>

        {/* Filter Section - Only shown when showFilters is true */}
        {showFilters && hasFiltersAvailable && (
          <div
            style={{
              padding: '12px 24px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#fafafa',
            }}
          >
            {filterCount > 0 && (
              <WidgetFilterChips
                filters={filters}
                onRemoveFilter={handleRemoveFilter}
                fieldLabels={fieldLabels}
              />
            )}
            {availableFilters.length > 0 ? (
              <button
                onClick={() => setFilterSelectorOpen(true)}
                style={{
                  marginTop: filterCount > 0 ? '8px' : '0',
                  padding: '6px 12px',
                  backgroundColor: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#1e40af',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#dbeafe';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#eff6ff';
                }}
              >
                <span>+</span>
                {filterCount > 0 ? 'Edit Filters' : 'Add Filter'}
              </button>
            ) : filterCount === 0 ? (
              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                No filters configured for this widget
              </div>
            ) : null}
          </div>
        )}

        {/* Content Area */}
        <div
          style={{
            flex: 1,
            overflow: 'hidden',
            minHeight: 0,
            padding: '16px',
          }}
        >
          {viewMode === 'chart' ? (
            // Chart View
            children ? (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                }}
              >
                {children}
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#6b7280',
                  fontSize: '14px',
                }}
              >
                No content available
              </div>
            )
          ) : (
            // Grid View
            <div
              style={{
                width: '100%',
                height: '100%',
                overflow: 'auto',
              }}
            >
              {drillToDetail.isLoading && drillToDetail.data.length === 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 24px',
                    color: '#6b7280',
                  }}
                >
                  <Loader2
                    size={40}
                    style={{
                      color: '#3b82f6',
                      animation: 'spin 1s linear infinite',
                    }}
                  />
                  <p style={{ marginTop: '16px', fontSize: '14px' }}>
                    Loading data...
                  </p>
                  <style>
                    {`
                      @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                      }
                    `}
                  </style>
                </div>
              ) : drillToDetail.error ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 24px',
                    color: '#991b1b',
                  }}
                >
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      backgroundColor: '#fef2f2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '16px',
                    }}
                  >
                    <span style={{ fontSize: '24px' }}>!</span>
                  </div>
                  <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                    Failed to load data
                  </p>
                  <p style={{ fontSize: '14px', color: '#dc2626', textAlign: 'center' }}>
                    {drillToDetail.error}
                  </p>
                </div>
              ) : drillToDetail.data.length === 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 24px',
                    color: '#6b7280',
                  }}
                >
                  <p style={{ fontSize: '14px' }}>No data available</p>
                </div>
              ) : (
                <DataGrid
                  columns={gridColumns}
                  data={drillToDetail.data}
                  showToolbar={false}
                  showPagination={drillToDetail.totalPages > 1}
                  manualPagination={true}
                  pageCount={drillToDetail.totalPages}
                  totalCount={drillToDetail.totalRows}
                  pagination={pagination}
                  onPaginationChange={handlePaginationChange}
                  sorting={sorting}
                  onSortingChange={handleSortingChange}
                  loading={drillToDetail.isLoading}
                  enableRowSelection={false}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Filter Selector Modal */}
      {filterSelectorOpen && availableFilters.length > 0 && (
        <WidgetFilterSelector
          isOpen={filterSelectorOpen}
          onClose={() => setFilterSelectorOpen(false)}
          availableFilters={availableFilters}
          currentFilters={filters}
          onSaveFilters={handleSaveFilters}
          widgetTitle={title}
        />
      )}
    </div>
  );

  // Use portal to render at document body level, ensuring proper overlay
  return createPortal(modalContent, document.body);
};

export default WidgetModal;
